import * as XLSX from 'xlsx';
import { ColumnDefinition } from '@/components/configure-columns-modal';

// Format date to DD/MM/YYYY
export const formatDateForExport = (date: Date | null | undefined): string => {
  if (!date) return '';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Escape CSV value
const escapeCSVValue = (value: any): string => {
  if (value === null || value === undefined) return '""';
  const stringValue = String(value);
  // Wrap in quotes if contains comma, quote, or newline
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return `"${stringValue}"`;
};

// Export to CSV
export const exportToCSV = (data: any[], columns: string[], filename: string) => {
  // Create CSV header row from column names
  const header = columns.map(col => escapeCSVValue(col)).join(',');
  
  // Create CSV data rows
  const rows = data.map(item => 
    columns.map(col => escapeCSVValue(item[col])).join(',')
  );
  
  // Combine header and rows
  const csv = [header, ...rows].join('\n');
  
  // Create Blob and trigger download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  
  // Clean up
  URL.revokeObjectURL(link.href);
};

// Export to Excel
export const exportToExcel = (data: any[], columns: string[], filename: string) => {
  // Prepare data with only specified columns
  const exportData = data.map(item => {
    const row: any = {};
    columns.forEach(col => {
      row[col] = item[col];
    });
    return row;
  });
  
  // Create worksheet from data
  const ws = XLSX.utils.json_to_sheet(exportData);
  
  // Apply column widths (auto-size)
  const wscols = columns.map(() => ({ wch: 15 }));
  ws['!cols'] = wscols;
  
  // Create workbook and add worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Stock Items');
  
  // Trigger download
  XLSX.writeFile(wb, filename);
};

// Get current date in YYYY-MM-DD format for filename
export const getCurrentDateForFilename = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Prepare data for export (format values and filter to specified columns)
export const prepareDataForExport = (items: any[], columns: ColumnDefinition[]): any[] => {
  return items.map(item => {
    // First, create a formatted version of the item with computed fields
    const formattedItem: Record<string, any> = {
      ...item,
      // Format dates
      lastMovementDate: formatDateForExport(item.lastMovementDate),
      lastPurchaseDate: formatDateForExport(item.lastPurchaseDate),
      lastSaleDate: formatDateForExport(item.lastSaleDate),
      // Format status
      status: item.inactive ? 'Inactive' : 'Active',
      // Calculate free quantity
      freeQuantity: item.physicalQuantity - item.allocatedQuantity,
      // Calculate gross margin percentage
      grossMarginPercent: item.unitPrice > 0 
        ? (((item.unitPrice - item.averageCost) / item.unitPrice) * 100).toFixed(1) + '%'
        : '0.0%',
    };
    
    // Then, extract only the columns we need, using the column name as the key
    const exportRow: Record<string, any> = {};
    columns.forEach(col => {
      exportRow[col.name] = formattedItem[col.id];
    });
    
    return exportRow;
  });
};

// Get visible columns for current view export
export const getVisibleColumns = (visibleColumnIds: string[], allColumns: ColumnDefinition[]): ColumnDefinition[] => {
  // Create a map from column ID to column definition for quick lookup
  const columnMap = new Map(allColumns.map(col => [col.id, col]));
  
  // Return column definitions in the order of visibleColumnIds
  return visibleColumnIds
    .map(id => columnMap.get(id))
    .filter((col): col is ColumnDefinition => col !== undefined);
};

// Get all columns for all data export
export const getAllColumns = (allColumns: ColumnDefinition[]): ColumnDefinition[] => {
  return allColumns;
};
